import { call, all, put, takeLatest, select } from 'redux-saga/effects';
import AsyncStorage from '@react-native-community/async-storage';

import api from '../../../services/api';
import notification from '../../../services/notification';
import history from '../../../services/history';
import userService from '../../../services/modules/user/user-service';
import * as userActions from './user-actions';
import { ISA_ERROR, ISA_TENANT_ID, USER_ROLE } from '../../../constants';
import {
  GET_ALL_REQUEST,
  GET_BY_ID_REQUEST,
  SAVE_REQUEST,
  EDIT_ITEM,
  DELETE_ITEM,
  UPDATE_DEVICE_REQUEST,
  DELETE_DEVICE_REQUEST
} from './user-constants';
import domainService from '../../../services/modules/domain/domain-service';
import { formatMessage } from '~/i18n/translate';
import { ConfigurationInterface } from '@e3labs/isa-data-dictionary';
import { AppStateType } from '../root-reducer';
import { formatCellphoneNumber } from '~/utils/format';
import { loadDomainSelects, ensureRestrictions } from '../select-pagination/select-pagination-service';

const INITIAL_VALUES = {
  accountBalance: false,
  currentBalanceAmount: 0,
  name: '',
  email: '',
  nickname: '',
  userRole: USER_ROLE.VENDOR,
  externalCode: '',
  roleExternalCode: '',
  inactive: false,
  superiorExternalCode: '',
  userSubsidiaries: [],
  password: '',
  passwordConfirm: '',
  pictureURL: '',
  changeImage: false,
  superior: null,
  providers: [],
  departiments: [],
  sections: [],
  brands: [],
  categories: [],
  canApproveDiscountScope: [],
  productLines: [],
  allowVendorTracking: false,
  useMarginControlByColor: false,
  phone1: '',
  phone2: ''
};

const sort = items => {
  return [...items]?.sort((a: any, b: any) => a?.externalCode - b?.externalCode);
};

export function* getAll() {
  try {
    yield put(userActions.setLoadingData(true));

    const { data } = yield call(userService.getUsers);
    if (data) {
      yield put(userActions.getAllSuccess([...data]));
    }

    yield put(userActions.setLoadingData(false));
  } catch (error) {
    yield put(userActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* getById({ id }: any) {
  try {
    yield put(userActions.setLoadingData(true));

    const configuration: ConfigurationInterface = yield select((state: AppStateType) => state.configuration);

    const [{ data: users }, { data: subsidiaries }, domains] = yield all([
      call(api.get, '/user/'),
      call(domainService.getSubsidiaries, {}),
      call(loadDomainSelects)
    ]);

    const supervisors = sort(
      users.filter(
        (user: any) =>
          user.userRole === USER_ROLE.SUPERVISOR ||
          user.userRole === USER_ROLE.MANAGER ||
          user.userRole === USER_ROLE.DIRECTOR
      )
    );

    const selects = {
      userRoles: userService.getUserRoles(),
      supervisors,
      subsidiaries: sort(subsidiaries)
    };

    if (!id) {
      yield put(
        userActions.getByIdSuccess({
          data: {
            ...INITIAL_VALUES,
            allowVendorTracking:
              configuration?.allowVendorTracking || configuration?.allowManualVendorTracking ? true : false,
            useMarginControlByColor: configuration?.useMarginControlByColor ? true : false
          },
          selects
        })
      );
    } else {
      const { data } = yield call(userService.getUser, id);

      if (data) {
        const { productRestrictions, productLineExternalCodes, contactInfo, ...rest } = data;
        const ensured = yield* ensureRestrictions(productRestrictions, domains);

        const { data: subsidiariesFilter } = yield call(domainService.getSubsidiaries, {
          userSubsidiaryFilter: data?.userSubsidiaries ?? []
        });
        yield put(
          userActions.getByIdSuccess({
            data: {
              ...rest,
              userSubsidiaries: data.userSubsidiaries
                ?.map((s: any) => subsidiariesFilter?.find((t: any) => t.externalCode === s))
                ?.filter(Boolean)
                ?.sort((a: any, b: any) => a.externalCode - b.externalCode),
              superior: supervisors.find((s: any) => s.roleExternalCode === data.superiorExternalCode),
              providers: ensured?.providers,
              departiments: ensured?.departments,
              sections: ensured?.sections,
              brands: ensured?.brands,
              categories: ensured?.categories,
              canApproveDiscountScope:
                data?.canApproveDiscountScope?.length > 0
                  ? data?.canApproveDiscountScope?.map(externalCode => ({
                      ...supervisors.find(c => c.externalCode === externalCode)
                    }))
                  : [],
              productLines: ensured?.productLines,
              allowVendorTracking:
                configuration?.allowVendorTracking || configuration?.allowManualVendorTracking
                  ? data?.allowVendorTracking === undefined
                    ? true
                    : data?.allowVendorTracking
                  : data?.allowVendorTracking
                  ? data?.allowVendorTracking
                  : false,
              useMarginControlByColor: configuration?.useMarginControlByColor
                ? data?.useMarginControlByColor === undefined
                  ? true
                  : data?.useMarginControlByColor
                : data?.useMarginControlByColor,
              phone1: formatCellphoneNumber(`${contactInfo?.phone1}`) ?? null,
              phone2: formatCellphoneNumber(`${contactInfo?.phone2}`) ?? null
            },
            selects
          })
        );
      }
    }
    yield put(userActions.setLoadingData(false));
  } catch (error) {
    yield put(userActions.setLoadingData(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* save(data: any) {
  try {
    yield put(userActions.setLoadingSend(true));
    const { id, payload, files } = data;

    if (
      (payload?.userRole === USER_ROLE.VENDOR ||
        payload?.userRole === USER_ROLE.TELEMARKETING ||
        payload?.userRole === USER_ROLE.SUPERVISOR) &&
      !payload.superior
    ) {
      yield call(notification.warning, 'Por favor, informe um superior!');
      yield put(userActions.setLoadingSend(false));
      return;
    }

    const tenantID: string = yield AsyncStorage.getItem(ISA_TENANT_ID);
    const {
      password,
      passwordConfirm,
      changeImage,
      devices,
      superior,
      externalCode,
      roleExternalCode,
      nickname,
      categories,
      departiments,
      sections,
      providers,
      brands,
      productLines,
      phone1,
      phone2,
      passwordHash,
      lastSync,
      extras,
      contactInfo,
      ...rest
    } = payload;

    const user = {
      ...rest,
      externalCode:
        (payload?.userRole === USER_ROLE.MANAGER || payload?.userRole === USER_ROLE.DIRECTOR) &&
        payload?.externalCode === ''
          ? roleExternalCode?.trim()?.replace(/\s/g, '')
          : payload?.userRole === USER_ROLE.TECH && payload?.externalCode === ''
          ? nickname
          : externalCode?.trim()?.replace(/\s/g, ''),
      roleExternalCode: roleExternalCode?.trim()?.replace(/\s/g, ''),
      nickname,
      superiorExternalCode:
        superior &&
        (payload?.userRole === USER_ROLE.VENDOR ||
          payload?.userRole === USER_ROLE.TELEMARKETING ||
          payload?.userRole === USER_ROLE.SUPERVISOR ||
          payload?.userRole === USER_ROLE.MANAGER)
          ? superior?.roleExternalCode?.trim()?.replace(/\s/g, '')
          : null,
      tenantID: payload?.tenantID ?? tenantID,
      userSubsidiaries:
        payload?.userSubsidiaries?.length > 0 ? [...payload?.userSubsidiaries?.map((s: any) => s?.externalCode)] : [],
      extras:
        payload?.password && payload?.passwordConfirm
          ? {
              password: payload?.password,
              passwordConfirm: payload?.passwordConfirm
            }
          : null,
      canApproveDiscountScope:
        payload?.canApproveDiscountScope?.length > 0 ? payload?.canApproveDiscountScope?.map(p => p?.externalCode) : [],
      productRestrictions: {
        productLineExternalCode: productLines?.length > 0 ? [...productLines?.map(p => p?.externalCode)] : [],
        providerExternalCode: providers?.length > 0 ? [...providers?.map(p => p?.externalCode)] : [],
        departmentExternalCode: departiments?.length > 0 ? [...departiments?.map(p => p?.externalCode)] : [],
        sectionExternalCode: sections?.length > 0 ? [...sections?.map(p => p?.externalCode)] : [],
        brandExternalCode: brands?.length > 0 ? [...brands?.map(p => p?.externalCode)] : [],
        categoryExternalCode: categories?.length > 0 ? [...categories?.map(p => p?.externalCode)] : []
      },
      contactInfo: {
        phone1: phone1 ? phone1?.replace(/[&/\\#,+()$~%.'" ():*?<>{}-]/g, '') : null,
        phone2: phone2 ? phone2?.replace(/[&/\\#,+()$~%.'" ():*?<>{}-]/g, '') : null
      }
    };

    if (id) {
      yield call(userService.editUser, { _id: id, ...user });
    } else {
      yield call(userService.createUser, user);
    }

    if (user?.externalCode) {
      if (payload.changeImage) yield call(userService.putImage, user?.externalCode, files);
    }

    yield call(history.push, `/access/user/`, {
      date: new Date().getTime()
    });
    yield put(userActions.setLoadingSend(false));
    yield call(
      notification.success,
      id ? formatMessage('userFormUpdatedSuccess') : formatMessage('userFormCreatedSuccess')
    );
  } catch (error) {
    yield put(userActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteItem({ id }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(userActions.setLoadingSend(true));
      yield call(userService.deleteUser, id);
      yield put(userActions.setLoadingSend(false));

      yield put(userActions.getAllRequest());
      yield call(notification.success, formatMessage('userFormExcludeSuccess'));
    }
  } catch (error) {
    yield put(userActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* editItem({ id }: any) {
  yield call(history.push, `/access/user/edit/${id}`);
}

export function* updateDevice({ userExternalCode, device }: any) {
  try {
    yield put(userActions.setLoadingSend(true));
    yield call(userService.editDevice, userExternalCode, device);
    yield put(userActions.updateDeviceSuccess(device));
    yield put(userActions.setLoadingSend(false));

    yield call(notification.success, formatMessage('userFormDeviceUpdatedSuccess'));
  } catch (error) {
    yield put(userActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export function* deleteDevice({ userExternalCode, deviceId }: any) {
  try {
    const { isConfirmed } = yield call(
      notification.confirm,
      formatMessage('commonLabelExclude'),
      formatMessage('commonLabelExcludeItem')
    );
    if (isConfirmed) {
      yield put(userActions.setLoadingSend(true));
      yield call(userService.deleteDevice, userExternalCode, deviceId);
      yield put(userActions.deleteDeviceSuccess(deviceId));
      yield put(userActions.setLoadingSend(false));

      yield call(notification.success, formatMessage('userFormDeviceExcludeSuccess'));
    }
  } catch (error) {
    yield put(userActions.setLoadingSend(false));
    yield put({ type: ISA_ERROR, error });
  }
}

export default all([
  takeLatest(GET_ALL_REQUEST, getAll),
  takeLatest(GET_BY_ID_REQUEST, getById),
  takeLatest(SAVE_REQUEST, save),
  takeLatest(DELETE_ITEM, deleteItem),
  takeLatest(EDIT_ITEM, editItem),
  takeLatest(UPDATE_DEVICE_REQUEST, updateDevice),
  takeLatest(DELETE_DEVICE_REQUEST, deleteDevice)
]);
